import { useCallback, useEffect, useMemo } from "react";

import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";

import { useAuth } from "../auth/AuthProvider";
import WorkspaceTabs from "../_src_shared/DashboardLayout/ui/WorkspaceTabs";
import { useFetchedSelectionsRequestsHook } from "../hooks/useFetchedSelectionsRequestsHook";
import { useWorkspaceTabs } from "../hooks/utils/useWorkspaceTabs";
import { nsKey } from "../utils/storage/partnerStorage";
import { RealtimeProvider } from "../realtime/RealtimeProvider";
import { usePermissionsHook } from "../hooks/usePermissionsHook";
import { useIsInUserActivityGroup } from "../hooks/useIsInUserActivityGroup";
import {
  DEFAULT_USERS_TAB,
  usersTabPath,
  visibleUsersTabs,
} from "../screens/UsersScreen/usersTabs";
import { useCanSeeSizers } from "../hooks/useCanSeeSizers";
import { ADMIN_NAV_ITEMS } from "./adminNav";
import { visibleSizerTabs } from "./sizersNav";
import TopHeader from "./TopHeader";
import WhatsNewProvider from "../screens/ChangelogScreen/WhatsNewProvider";

const AuthenticatedDashboard = ({ children }) => {
  const {
    user: authUser,
    logout,
    isImpersonating,
    stopImpersonation,
  } = useAuth();
  const navigate = useNavigate();
  const {
    isCapitalMarkets,
    isCloser,
    isUnderwriter,
    isAdmin,
    isSuperAdmin,
    canAccessUsersTab,
  } = usePermissionsHook();
  // Reveals the Users > Activity entry. Same hook the screen uses, so the menu
  // and the tab agree; it fails closed while the group lookup is in flight.
  const { allowed: canSeeActivity } = useIsInUserActivityGroup();
  const { allowed: canSeeSizers } = useCanSeeSizers();

  // The header shows the authenticated (digital-sizer) user. Map its shape
  // (full_name / email) to what TopHeader expects (fullName / emailAddress).
  const headerUser = useMemo(
    () => ({
      fullName: authUser?.full_name,
      emailAddress: authUser?.email,
    }),
    [authUser],
  );

  const { tabs: pinnedTabs } = useWorkspaceTabs();
  const pinnedCount = pinnedTabs?.length || 0;
  const [pinnedOpen, setPinnedOpen] = useLocalStorageState(
    nsKey("lo-workspace-tabs-open"),
    { defaultValue: false },
  );
  const togglePinned = useCallback(
    () => setPinnedOpen((v) => !v),
    [setPinnedOpen],
  );

  const handleLogoutUser = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  // Load reference data once into Redux (fetchedSelections) so pickers use live
  // data instead of static fallbacks. Both are cached in localStorage with a
  // 5-day TTL, so this only hits the network when the cache is missing/expired:
  //   - GET /underwriting/file_doc_types        → doc-type pickers
  //   - GET /model_schemas/object/underwriting  → loan model dropdown options
  //
  // NOTE: `users` (GET /users/all_users) and `correspondents`
  // (GET /correspondents/list) are deliberately NOT fetched here. They used to
  // load eagerly for every page, so a screen that never shows them (e.g.
  // Support) still paid for both requests on load. They're now pulled lazily by
  // the screens that consume them via useEnsureUsers / useEnsureCorrespondents
  // (see src/hooks/useEnsureReferenceData.js) — cache-gated so navigating back
  // to a screen doesn't refetch.
  const { getLoanFileDocGroupsAndTypes, getModelSelectOptions } =
    useFetchedSelectionsRequestsHook();
  useEffect(() => {
    getLoanFileDocGroupsAndTypes({});
    // Cache-gated: reads the partner-scoped localStorage entry when fresh
    // (5-day TTL) and only hits the network when it's missing/expired.
    getModelSelectOptions({});
    // Run once on mount; the fetch helpers aren't memoized so they're
    // intentionally excluded from deps to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sizerTabs = useMemo(
    () => visibleSizerTabs({ canUseDscr: isSuperAdmin }),
    [isSuperAdmin],
  );

  const navItems = useMemo(
    () => [
      // Header tab order: Pre-Close, Closing, Acquisitions, Lock Queue,
      // Appraisal Queue, Applications, Correspondents, Users, Support, Admin.
      //
      // Pre-Close / Closing pipeline nav — synced from Eastview (replaces the
      // former Lendmarq "Pipeline" dropdown). Pre-Close is a flat tab; Closing's
      // caret opens Clear to Close / Calendar / Closed Loans. The Lendmarq
      // Full/Active/Closed/Dead-Inactive bucket board still lives at
      // /loans + /loans/pipeline/* (it stays the default landing + catch-all
      // target) but is no longer a header tab.
      { to: "/loans/pre-close", label: "Pre-Close" },
      {
        to: "/loans/closing",
        label: "Closing",
        // Clicking the tab still lands on the Clear to Close pipeline (default);
        // the caret opens a dropdown for the calendar and the closed-loans board.
        menu: [
          { to: "/loans/closing", label: "Clear to Close" },
          { to: "/loans/closing/calendar", label: "Calendar" },
          { to: "/loans/closed", label: "Closed Loans" },
        ],
      },
      // TEMP: Post Close tab hidden from the header nav.
      // { to: "/loans/post-close", label: "Post Close" },
      // TEMP: Lock Requests tab hidden from the header nav.
      // { to: "/loans/loan-lock-requests", label: "Lock Requests" },
      // Acquisitions (postclose) — closed loans purchased from correspondents.
      // TEMP: Wire Queue tab hidden from the nav; the Acquisitions tab goes
      // straight to the pipeline for now. Restore the dropdown to bring it back:
      //   menu: [
      //     { to: "/acquisitions", label: "Pipeline" },
      //     { to: "/acquisitions-wire-queue", label: "Wire Queue" },
      //   ],
      // (The /acquisitions-wire-queue route stays mounted — only the nav entry
      // is hidden.)
      { to: "/acquisitions", label: "Acquisitions" },
      // Full Pipeline — Pre-Close, Closing, Closed and Acquisitions on one board.
      { to: "/loans/full-pipeline", label: "Full Pipeline" },
      // Sizers — admins + super admins, plus members of the "Sizer Visibility"
      // group (hooks/useCanSeeSizers.js). Clicking the tab lands on Priced
      // Scenarios; the caret opens the sizers. SizersLayout re-checks the gate.
      ...(canSeeSizers
        ? [
            {
              to: sizerTabs[0].to,
              label: "Sizers",
              // DSCR Sizer is super-admin only (sizersNav.visibleSizerTabs).
              menu: sizerTabs,
            },
          ]
        : []),
      // Lock Queue — visible to the capital-markets group + closers, plus
      // super admins (see SUPER_ADMIN_EMAILS in hooks/usePermissionsHook.js).
      ...(isCapitalMarkets || isCloser || isSuperAdmin
        ? [{ to: "/lock-queue", label: "Lock Queue" }]
        : []),
      // Appraisal Queue — the internal-appraisal team's worklist. Internal
      // staff only (underwriting / admin).
      ...(isUnderwriter || isAdmin
        ? [{ to: "/appraisal-queue", label: "Appraisal Queue" }]
        : []),
      // Fundings — the Capital Markets "ready to fund?" surface (forward view,
      // funding outlook, funding tapes). Same gate as the Lock Queue.
      ...(isCapitalMarkets || isCloser || isSuperAdmin
        ? [{ to: "/fundings", label: "Fundings" }]
        : []),
      // Submissions — the borrower-submission intake queue ported from tpo
      // (loan applications + credit authorizations, assign to loans). Clicking
      // the tab opens the full queue (All lane); the caret deep-links to a
      // single lane. Replaces the former "Applications" tab and its
      // /applications routes, which now redirect here. (Lendmarq-only.)
      {
        to: "/submissions",
        label: "Submissions",
        menu: [
          { to: "/submissions", label: "All Submissions" },
          { to: "/submissions/by-borrower", label: "By Borrower" },
          { to: "/submissions/applications", label: "Loan Applications" },
          { to: "/submissions/credit-auths", label: "Credit Auths" },
        ],
      },
      // Correspondents — admins only. The /correspondents route itself stays
      // mounted in index.js for direct-URL access.
      ...(isAdmin ? [{ to: "/correspondents", label: "Correspondents" }] : []),
      // Users — super admins only, plus the extra per-email grants (see
      // ALLOW_USERS_TAB_EMAILS in hooks/usePermissionsHook.js). The /users
      // route itself stays open.
      // Synced to Eastview's data-driven dropdown: the menu mirrors the
      // screen's own tabs, built from the SAME list
      // (screens/UsersScreen/usersTabs.js) so the two can't drift, Activity
      // included -- it appears only for members of the "User Activity" group.
      // (Replaces the former Lendmarq LOS Users / Borrower Portal Users menu;
      // /borrower-portal-users stays mounted in index.js for direct-URL access.)
      ...(canAccessUsersTab
        ? [
            {
              to: usersTabPath(DEFAULT_USERS_TAB.slug),
              label: "Users",
              menu: visibleUsersTabs({ canSeeActivity }).map((t) => ({
                to: usersTabPath(t.slug),
                label: t.label,
              })),
            },
          ]
        : []),
      // User Activity, standalone — for group members who are NOT on the Users
      // allowlist. Sending them to /users/activity would sit them beside three
      // tabs that 403 for them, so they get the view on its own page instead.
      //
      // Deliberately EXCLUSIVE with the entry above: someone with both gets it
      // as a Users tab and nothing here, so the same view never appears twice
      // in the nav.
      ...(!canAccessUsersTab && canSeeActivity
        ? [{ to: "/user-activity", label: "User Activity" }]
        : []),
      {
        to: "/support",
        label: "Support",
        menu: [
          { to: "/support", label: "Support" },
          { to: "/changelog", label: "Changelog" },
          // Lendmarq divergence (see CLAUDE.md #11): no Request Board entry.
          // Eastview lists it here for super admins, but the `/request_board`
          // router exists only in eastview-os — lendmarq-os has none, so every
          // call would 404. Hidden for all users until the backend ships it.
          // Dev-only API endpoint tester — hidden from production builds.
          ...(process.env.NODE_ENV === "development"
            ? [{ to: "/endpoint-test", label: "API Test" }]
            : []),
        ],
      },
      // Admin — pricing config + internal tooling (Sizer, DSCR, Grids, Rules,
      // Guidelines, Task Lists, Banned Appraisers). Admins only (these screens
      // hit admin-gated endpoints). Clicking the tab lands on Pricing; the caret
      // opens the rest. Routes are mounted in index.js regardless.
      ...(isAdmin
        ? [
            {
              to: ADMIN_NAV_ITEMS[0].to,
              label: "Admin",
              menu: ADMIN_NAV_ITEMS,
            },
          ]
        : []),
    ],
    [
      isCapitalMarkets,
      isCloser,
      isUnderwriter,
      isAdmin,
      isSuperAdmin,
      canAccessUsersTab,
      canSeeActivity,
      canSeeSizers,
      sizerTabs,
    ],
  );

  // NOTE (auth swap): getLoanFileDocGroupsAndTypes is re-enabled above now that
  // eastview-los serves /underwriting/file_doc_types under the first-party JWT.
  // getLoggedInUser / getAllUsers remain disabled (header user comes from
  // AuthProvider; /auth/me is mirrored into Redux there) — re-enable those once
  // their eastview-los equivalents are wired.

  return (
    <RealtimeProvider>
      {/* Release "What's new" modal (super admins only for now), wrapping the
          shell so the Changelog page can reopen it. */}
      <WhatsNewProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {isImpersonating && (
          <Box
            sx={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              flexWrap: "wrap",
              px: 2,
              py: 0.75,
              background: "#9a3412",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Box>
              ⚠️ You are impersonating{" "}
              <strong>{authUser?.email || "another user"}</strong>. Actions are
              taken as this user.
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => stopImpersonation()}
              sx={{
                font: "inherit",
                fontSize: 12,
                fontWeight: 700,
                color: "#9a3412",
                background: "#fff",
                border: "none",
                borderRadius: "6px",
                px: "12px",
                py: "4px",
                cursor: "pointer",
                "&:hover": { background: "#ffedd5" },
              }}
            >
              Stop impersonating
            </Box>
          </Box>
        )}
        <TopHeader
          navItems={navItems}
          user={headerUser}
          onLogout={handleLogoutUser}
          pinnedCount={pinnedCount}
          pinnedOpen={pinnedOpen}
          onTogglePinned={togglePinned}
        />
        {pinnedOpen && pinnedCount > 0 && (
          <WorkspaceTabs onToggle={togglePinned} />
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 0,
            position: "relative",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
      </Box>
      </WhatsNewProvider>
    </RealtimeProvider>
  );
};

export default AuthenticatedDashboard;
